export interface FloatingMenuViewport {
  left: number
  top: number
  width: number
  height: number
}

export interface FloatingMenuRect {
  left: number
  top: number
  right: number
  bottom: number
  width: number
}

interface FloatingMenuPositionInput {
  trigger: FloatingMenuRect
  menuWidth: number
  menuHeight: number
  viewport: FloatingMenuViewport
  placement: 'top' | 'bottom'
  gap?: number
  padding?: number
  heightLimit?: number
}

export interface FloatingMenuPosition {
  left: number
  top: number
  width: number
  maxHeight: number
  placedAbove: boolean
}

const clamp = (value: number, minimum: number, maximum: number) => Math.min(Math.max(value, minimum), Math.max(minimum, maximum))

export function calculateFloatingMenuPosition({
  trigger,
  menuWidth,
  menuHeight,
  viewport,
  placement,
  gap = 6,
  padding = 10,
  heightLimit = 260,
}: FloatingMenuPositionInput): FloatingMenuPosition {
  const safeLeft = viewport.left + padding
  const safeTop = viewport.top + padding
  const safeRight = viewport.left + viewport.width - padding
  const safeBottom = viewport.top + viewport.height - padding
  const width = Math.min(Math.max(menuWidth, trigger.width), Math.max(0, safeRight - safeLeft))
  const availableAbove = Math.max(0, trigger.top - gap - safeTop)
  const availableBelow = Math.max(0, safeBottom - trigger.bottom - gap)
  const preferredSpace = placement === 'bottom' ? availableBelow : availableAbove
  const alternateSpace = placement === 'bottom' ? availableAbove : availableBelow
  const usePreferredSide = menuHeight <= preferredSpace || preferredSpace >= alternateSpace
  const placedAbove = placement === 'top' ? usePreferredSide : !usePreferredSide
  const availableHeight = placedAbove ? availableAbove : availableBelow
  const maxHeight = Math.min(heightLimit, availableHeight)
  const renderedHeight = Math.min(menuHeight, maxHeight)
  const desiredLeft = trigger.right - width
  const left = clamp(desiredLeft, safeLeft, safeRight - width)
  const top = placedAbove
    ? Math.max(safeTop, trigger.top - gap - renderedHeight)
    : trigger.bottom + gap

  return { left, top, width, maxHeight, placedAbove }
}
